import React, { PropsWithChildren } from 'react'
import styled from 'styled-components'
import cx from 'clsx'

import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { accountAccessShowLogin } from 'actions/UiActions'
import { rem } from 'polished'
import IconDetail from 'components/Common/IconDetail'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'components/Luxkit/Button/TextButton'
import { hasPassedWalledGarden } from 'selectors/accountSelectors'
import useShowNewHotelSearch from 'components/SearchV2/Hooks/useShowNewHotelSearch'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import { useTranslation } from 'react-i18next'
import LineTagAltIcon from 'components/Luxkit/Icons/line/LineTagAltIcon'

const Root = styled.div`
  display: flex;
  flex-direction: column;
`

const BottomWrapper = styled.div`
  display: flex;
  margin-top: ${rem(12)};
  justify-content: flex-end;

  ${mediaQueryUp.md} {
    margin-top: ${rem(16)};
  }

  > * + * {
    margin-left: ${rem(8)};
  }

  &.align-left:not(.full-width) {
    justify-content: flex-start;
  }

  &.align-center:not(.full-width) {
    justify-content: center;
  }

  &.align-right:not(.full-width) {
    justify-content: flex-end;
  }
`

const StyledIconDetail = styled(IconDetail)`
  font-size: ${props => props.theme.fontSize.xm};

  ${mediaQueryUp.xs} {
    font-size: ${props => props.theme.fontSize.sm};
  }

  &.align-left {
    justify-content: flex-start;
  }

  &.align-center {
    justify-content: center;
  }

  &.align-right {
    justify-content: flex-end;
  }
`

const StyledPriceTagIcon = styled(LineTagAltIcon)`
  height: ${rem(14)};
  width: ${rem(14)};

  ${mediaQueryUp.xs} {
    height: ${rem(16)};
    width: ${rem(16)};
  }
`

const BtnSignUp = styled(TextButton)`
  min-width: ${rem(100)};

  &.full-width {
    flex: 1;
  }
`

const BtnLogin = styled(TextButton)`
  min-width: ${rem(100)};

  &.full-width {
    flex: 1;
  }
`

interface Props {
  showContent?: boolean;
  loginText?: string;
  signUpText?: string;
  discountPercentage?: number;
  align?: 'left' | 'center' | 'right' | 'none';
  fullWidth?: boolean;
  showNewTileOverride?: boolean;
}

function OfferTileLoggedInButtons({
  showContent,
  children,
  loginText,
  signUpText,
  discountPercentage,
  align,
  fullWidth,
  showNewTileOverride,
}: PropsWithChildren<Props>) {
  const dispatch = useAppDispatch()

  const showLogin = e => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(accountAccessShowLogin())
  }
  const { t } = useTranslation(['common', 'offerPage'])
  const showNewTile = useShowNewHotelSearch() || (showNewTileOverride ?? false)

  const shouldShowLoggedOutPrice = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchShowLoggedOutPrices)

  if (showContent || shouldShowLoggedOutPrice) {
    return <>{children}</>
  }

  const alignClass = `align-${align}`

  return <Root >
    <StyledIconDetail variant="body-medium" weight="semi-bold" className={alignClass}>
      <StyledPriceTagIcon />
      {discountPercentage && discountPercentage > 0 ? t('memberDiscountPercentage', { ns: 'offerPage', discountPercent: discountPercentage }) : t('Members get access to exclusive rates', { ns: 'offerPage' })}
    </StyledIconDetail>
    <BottomWrapper className={cx({ 'full-width': fullWidth, [alignClass]: !fullWidth })}>
      {showNewTile && <BtnSignUp
        kind="secondary"
        // default to full width unless false is explicitly passed
        className={cx({ 'full-width': fullWidth !== false })}
        onClick={showLogin}>
        {signUpText ?? t('Sign up for free', { ns: 'common' })}
      </BtnSignUp>}
      {!showNewTile && <>
        <BtnLogin
          kind="secondary"
          className={cx({ 'full-width': fullWidth })}
          onClick={showLogin}>
          {loginText ?? t('Log in', { ns: 'common' })}
        </BtnLogin>
        <BtnSignUp
          kind="primary"
          className={cx({ 'full-width': fullWidth })}
          onClick={showLogin}
        >
          {signUpText ?? t('Sign up', { ns: 'common' })}
        </BtnSignUp>
      </>}
    </BottomWrapper>
  </Root >
}

OfferTileLoggedInButtons.defaultProps = {
  align: 'center',
} as Partial<PropsWithChildren<Props>>

const mapStateToProps = (state: App.State, props: Props) => ({
  showContent: hasPassedWalledGarden(state) || props.showContent,
})

export default connect(mapStateToProps)(OfferTileLoggedInButtons)
